/**
 * Tournament Creation Article Content
 */

export const tournamentCreationContent = `---
id: tournament-creation-guide
title: How to create a bowling tournament
description: What events, squads, handicap, and payout ratio mean in this app, and how to get from Create Tournament to Setup.
category: tournament-management
type: tutorial
estimatedTime: 8 min read
path: /support/tutorials/create-tournament
lastUpdated: 2026-09-04
version: 3.1
tags:
  - tournament-creation
  - tournament-setup
  - events
  - squads
  - handicap
  - payout-ratio
seo:
  title: How to create a bowling tournament
  description: How events and squads work in this app, and what handicap and payout ratio mean when you create a tournament.
  keywords:
    - bowling tournament creation
    - tournament setup
    - events
    - squads
    - handicap
    - payout ratio
    - bowling brackets
---

# How to create a bowling tournament

Click **Create Tournament**. Log in if you have an account. Create an account if you do not. New accounts create an organization in that same flow. After you save, the app opens **Setup**.

## Handicap

Handicap is extra pins added to a bowler's scratch score.

Formula: (base − average) × percentage, then rounded down. If you set a maximum, the result cannot go above that number.

Example: base 220, 90%, maximum 75. A 180 average gets (220 − 180) × 90% = 36.

You can change these values later on **Settings**.

## Payout ratio

1 in 6 means you plan to pay one place for every six entries. 1 in 4 means one place for every four entries.

That number is used when the app suggests how many places to pay. It is not the prize amounts. Amounts come from the **payout table** percentages times each event's prize fund. You can still change places and dollars on **Finance → Payouts** before you lock.

You can change the ratio later on **Settings**. You do not need a payout table before you add teams.

## Events

An event is something bowlers enter and the app scores. There are two kinds:

**Primary events** are the main contest: singles (one bowler), doubles (two), or team (three or more). Each has a set number of games.

**Side pots** are extra contests on those same games, such as high game or high series, scratch or handicap. All event handicap and all event scratch combine scores across primary events. You can add all events only if you have at least two different primary events.

Add them from the templates on **Setup**. Enter fees on the same screen. Split the entry into parts (prize fund, linage, and others). The prize-fund part is what gets paid out. The other parts are collected but are not prize money.

Some side pots are copied onto each squad when you create squads. You do not add those by hand for every squad.

Placement rules control whether the same bowler or team can cash more than once in that event (for example, once only, or more than once with a different partner).

## Squads

A squad is one session: a bowling center, a date, and a start time. Teams bowl on a squad. Scores belong to that squad.

Each squad has a default primary event. A team can still enter other events on that squad.

You can create many squads at once by choosing bowling centers, dates, and times, then edit one squad if it is different.

You can add teams on **Scoring** after at least one squad exists.

## Brackets

Optional. Add a bracket on **Setup**. Traditional 8-player trees only. Scratch and handicap are separate. Sell tickets on **Create Team**, **Edit Team**, or **Brackets**. See [How to run bowling brackets](/support/tutorials/run-brackets).

## Online signups

Optional. If you add teams yourself, use **Scoring**. If bowlers register themselves, turn signups on under Setup. They use **Signups**. Open **Signup Dashboard** to see who is on each squad.

Names are one full name. There is no separate last-name field.

## After Setup

Scoring, averages, payouts, and results are in [How to run a bowling tournament](/support/tutorials/run-a-tournament). Brackets are in [How to run bowling brackets](/support/tutorials/run-brackets).

---

**[Create a tournament](/create-tournament)**

- [Getting started](/support/tutorials/getting-started)
- [How to run bowling brackets](/support/tutorials/run-brackets)
- [Frequently asked questions](/support/faqs)
`;

export default tournamentCreationContent;
